import React, { useEffect } from "react";

import * as Sentry from "@sentry/nextjs";
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { NextWebVitalsMetric } from "next/app";
import Script from "next/script";
import { IBM_Plex_Sans_Arabic } from "next/font/google";
import { ThemeProvider } from "next-themes";
import { IBM_Plex_Sans } from "next/font/google";
import Router, { useRouter } from "next/router";
import { GoogleTagManager } from "@next/third-parties/google";
import ProgressBar from "@badrap/bar-of-progress";
import { CartProvider } from "hooks/useCartV2";
import { useLang } from "hooks/useLang";
// import { generateDefaultSeo } from "next-seo/pages";
import axios from "axios";
import Head from "next/head";

import { ArSEO, EnSEO } from "seo";
import { LogoSchema, WebsiteSchema, OrganizationSchema } from "seo/schema";

import FeatureFlagProvider from "providers/FeatureFlagProvider";

import { useMaintenanceMode } from "hooks/useMaintenanceMode";

import { TransferProvider } from "context/TransferContext";
import { UserProvider } from "context/UserContext";
import { ModalProvider } from "context/modalContext";

import UnifiedAnalyticsProvider from "components/Analytics/UnifiedAnalyticsProvider";
import MaintenanceScreen from "components/page_components/MaintenanceScreen";
import ErrorFallback from "components/atoms/ErrorFallback";
// import HelpButton from "components/atoms/HelpButton";
import ErrorBoundary from "components/page_components/ErrorBoundary";
import "./../styles/global.scss";
import "./../styles/index.scss";
import PulseWhatsapp from "components/PulseWhatsapp";
import useWindowSize from "hooks/useWindowSize";

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  release: `${process.env.NEXT_PUBLIC_APP_VERSION}-${process.env.NEXT_PUBLIC_FEATURE_ENVIRONMENT}`,
  integrations: [],
  tracesSampleRate: 0.2,
  replaysOnErrorSampleRate: 1.0,
  environment: process.env.NEXT_PUBLIC_FEATURE_ENVIRONMENT || "local",
});

const ibmPlexSansArabic = IBM_Plex_Sans_Arabic({
  weight: ["200", "400", "600"],
  display: "swap",
  style: "normal",
  subsets: ["latin"],
});

const ibmPlexSans = IBM_Plex_Sans({
  weight: ["200", "400", "600"],
  display: "swap",
  style: "normal",
  subsets: ["latin"],
});

export function reportWebVitals(metric: NextWebVitalsMetric) {
  // Use our analytics service to report Web Vitals
  if (typeof window !== "undefined" && window.analytics) {
    try {
      window.analytics.track({
        name: metric.name,
        properties: {
          category:
            metric.label === "web-vital"
              ? "Web Vitals"
              : "Next.js custom metric",
          value: Math.round(
            metric.name === "CLS" ? metric.value * 1000 : metric.value
          ),
          label: metric.id,
          nonInteraction: true,
        },
      });
    } catch (error) {
      console.error("Error tracking web vitals:", error);
    }
  }
}

export default function App({ Component, pageProps }) {
  const { isAr } = useLang();
    const { isMobile } = useWindowSize();
  const isInMaintenance = useMaintenanceMode(pageProps.isInMaintenance);


  const router = useRouter();

  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      document.body.classList.add("safari");
    }

    return () => {
      if (isSafari) {
        document.body.classList.remove("safari");
      }
    };
  }, []);

  const [queryClient] = React.useState(() => new QueryClient());

  const resetError = () => {
    router.push("/");
  };
  const handleError = (error: Error) => {
    Sentry.captureException(error);
    throw new Error("Sentry Frontend Error");
  };

  const { locale } = useRouter();
  const dir = locale === "ar-SA" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.dir = dir;
  }, [dir]);

  const progress = new ProgressBar({
    size: "2px",
    color: "#FD0058",
    className: "z-[199]",
    delay: 80,
  });
  Router.events.on("routeChangeStart", progress.start);
  Router.events.on("routeChangeComplete", progress.finish);
  Router.events.on("routeChangeError", progress.finish);
  const seo = isAr ? ArSEO : EnSEO;

  axios.defaults.headers.common["x-from-client"] = "frontend";
  axios.defaults.headers.common["x-client-version"] =
    process.env.NEXT_PUBLIC_APP_VERSION;
  axios.defaults.headers.common["x-client-environment"] = process.env.NODE_ENV;
  axios.defaults.headers.common["accept-language"] = isAr ? "ar" : "en";

  return (
    <>
      <FeatureFlagProvider>
        <UnifiedAnalyticsProvider>
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={pageProps.dehydratedState}>
              <ModalProvider>
                <UserProvider>
                  <CartProvider>
                    <ErrorBoundary
                      FallbackComponent={ErrorFallback}
                      onReset={resetError}
                      onError={handleError}
                    >
                      <ThemeProvider
                        defaultTheme="dark"
                        attribute="class"
                        forcedTheme={Component.theme || null}
                      >
                        {false ? (
                          <MaintenanceScreen />
                        ) : (
                          <>
                            {/* <Head>{generateDefaultSeo(seo)}</Head> */}
                            {/* Schema.org structured data */}
                            <LogoSchema />
                            <WebsiteSchema />
                            <OrganizationSchema />
                            <GoogleTagManager
                              dataLayerName="dataLayerAds"
                              gtmId={process.env.NEXT_PUBLIC_GA_TRACKING_ADD_ID}
                            />
                            {/* <SmartBanner /> */}
                            <TransferProvider>
                              <div
                                lang={locale}
                                dir={dir}
                                className={isAr ? ibmPlexSansArabic.className : ibmPlexSans.className}
                              >
                                <Script
                                  id="ua-support"
                                  strategy="afterInteractive"
                                  dangerouslySetInnerHTML={{
                                    __html: `
													var ua = window.navigator.userAgent;
													var isIE = /MSIE|Trident/.test(ua);
													
													if ( isIE ) {
														window.location = window.location.protocol + '//' + window.location.host + '/notsupported.html';
													}`,
                                  }}
                                />
                                {/* <ReactQueryDevtools
                                                                    initialIsOpen={
                                                                        false
                                                                    }
                                                                /> */}

                                <Component {...pageProps} />
                     <div
  className={`fixed bottom-10 z-50 block ${
    isAr
      ? isMobile
        ? "left-2"
        : "left-7"
      : isMobile
      ? "right-2"
      : "right-7"
  }`}
>

  <PulseWhatsapp/>
</div>
                              </div>
                            </TransferProvider>
                          </>
                        )}
                      </ThemeProvider>
                    </ErrorBoundary>
                  </CartProvider>
                </UserProvider>
              </ModalProvider>
            </HydrationBoundary>
          </QueryClientProvider>
        </UnifiedAnalyticsProvider>
      </FeatureFlagProvider>
    </>
  );
}